import { useState } from 'react';

import type { GarmentJob } from '@/api/garmentJobs';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass, textAreaClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';
import {
  closeWords,
  closeDraft,
  closeProblems,
  closeRequest,
  type CloseAction,
  type CloseRequest,
} from '@core/jobs/close';

// The wording and the refusals are the core's (`jobs/close.ts`); the phone's job sheet uses them too.
export type { CloseAction } from '@core/jobs/close';

/**
 * Closing, rejecting or extending one job.
 *
 * <p>
 * One panel for four actions because they differ by two fields and a sentence, and four drawers
 * would be four places for the wording about what happens to the block to drift apart. The
 * consequence of each is stated rather than left to the verb — "send back" and "finish" look alike
 * on a busy counter and do opposite things to the availability calendar.
 * </p>
 */
export function CloseJobDrawer({
  job,
  action,
  saving,
  onCommit,
  onClose,
}: {
  job: GarmentJob;
  action: CloseAction;
  saving: boolean;
  onCommit: (payload: CloseRequest) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(() => closeDraft(job));
  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const { cost, notes, dueOn } = draft;
  const setCost = (value: string) => setDraft((was) => ({ ...was, cost: value }));
  const setNotes = (value: string) => setDraft((was) => ({ ...was, notes: value }));
  const setDueOn = (value: string | null) => setDraft((was) => ({ ...was, dueOn: value }));

  const focusRef = useAutoFocusFirstField<HTMLDivElement>();
  const copy = closeWords(job, action);

  // The server refuses these, and saying so here is what turns a 400 after the click into a
  // disabled button before it.
  const reasonRequired = action === 'reClean' || action === 'damaged';
  const problems = closeProblems(action, draft, today);

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
        onClick={onClose}
      />

      <div
        ref={focusRef}
        {...enterKeyScope}
        className="relative flex h-full w-full max-w-[560px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg"
      >
        <div className="border-b border-border-color p-4">
          <h2 className="text-base font-semibold text-gray-900">{copy.title}</h2>
          <p className="mt-0.5 text-[13px] text-default">
            {job.garmentName} · <span className="font-mono-nums">{job.serialCode}</span>
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <Notice tone={action === 'damaged' ? 'warning' : 'info'} title="What this does">
            {copy.hint}
          </Notice>

          {action === 'extend' && (
            <Field label="Due back" hint="Today or later, and within a year." required>
              <DatePicker
                className="w-full"
                value={toDayjs(dueOn) ?? null}
                onChange={(value) => setDueOn(toIsoDate(value) ?? null)}
              />
            </Field>
          )}

          {action === 'complete' && (
            <Field
              label="What it cost"
              hint="Leave blank if the bill has not come yet — the garment goes back on the rail either way."
            >
              <input
                type="number"
                min={0}
                step="0.01"
                className={controlClass}
                value={cost}
                // A wheel over a number input silently rewrites it while the panel is scrolled past.
                onWheel={(event) => event.currentTarget.blur()}
                onChange={(event) => setCost(event.target.value)}
              />
            </Field>
          )}

          <Field
            label={reasonRequired ? 'What was wrong with it' : 'Anything worth recording'}
            hint={
              reasonRequired
                ? 'Required. This is what a disagreement with the vendor is settled from a month later.'
                : undefined
            }
            required={reasonRequired}
          >
            <textarea
              className={textAreaClass}
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>

          {/* A date or a figure that would be refused says so; the missing reason is the field's
              own "required", so it is not said twice. */}
          {((action === 'extend' && dueOn) || (action === 'complete' && cost.trim() !== '')) &&
            problems.length > 0 && <Notice tone="warning" title={problems[0] ?? ''} />}
        </div>

        <div className="flex justify-end gap-2 border-t border-border-color p-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={action === 'damaged' ? 'danger' : 'primary'}
            shortcut="save"
            data-enter-key-commit
            disabled={saving || problems.length > 0}
            onClick={() => onCommit(closeRequest(action, draft))}
          >
            {copy.commit}
          </Button>
        </div>
      </div>
    </div>
  );
}
